import { useId, useState, useRef, useEffect } from "react";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { format } from "date-fns";
import { useGRCSearch } from "@/services/grc";
import type { GRCSearchResult, AdvancedFilterCriteria } from "@/types/grc";
import { cn } from "@/utils/cn";

interface GRCSearchBoxProps {
  onSelect: (result: GRCSearchResult) => void;
  onClear: () => void;
}

/**
 * Autocomplete search for GRC Dashboard.
 * - Fires when input length >= 4 (mirrors Blazor ReadItems)
 * - Advanced filter flyout for refining by meeting type, planner, advisor, date range
 * - Toggle: show future events only / include past
 */
export function GRCSearchBox({ onSelect, onClear }: GRCSearchBoxProps) {
  const inputId = useId();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [showFuture, setShowFuture] = useState(true);
  const [open, setOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advanced, setAdvanced] = useState<AdvancedFilterCriteria>({});
  const [dateError, setDateError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: searchData } = useGRCSearch(query, !showFuture);

  // Close dropdown when clicking outside
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  let results = searchData?.searchResults ?? [];

  // Apply advanced client-side filters
  if (Object.values(advanced).some(Boolean)) {
    results = results.filter((r) => {
      if (advanced.meetingName && !r.eventName.toLowerCase().includes(advanced.meetingName.toLowerCase())) return false;
      if (advanced.meetingType && r.meetingType !== advanced.meetingType) return false;
      if (advanced.meetingPlannerName && r.meetingPlannerName !== advanced.meetingPlannerName) return false;
      if (advanced.businessAdvisor && r.businessAdvisor !== advanced.businessAdvisor) return false;
      if (advanced.startDate && r.eventEndDate < advanced.startDate) return false;
      if (advanced.endDate && r.eventStartDate > advanced.endDate) return false;
      return true;
    });
  }

  function handleSelect(result: GRCSearchResult) {
    setQuery(result.eventName);
    setOpen(false);
    onSelect(result);
  }

  function handleClear() {
    setQuery("");
    setAdvanced({});
    setDateError(null);
    setOpen(false);
    onClear();
  }

  function validateDates(start?: string | null, end?: string | null) {
    if (start && end && start > end) {
      setDateError("End date must be on or after start date.");
      return false;
    }
    setDateError(null);
    return true;
  }

  const hasActiveAdvanced = Object.values(advanced).some(Boolean);

  return (
    <div ref={wrapperRef} className="relative flex items-center gap-2">
      {/* Main input */}
      <div className="relative">
        <label htmlFor={inputId} className="sr-only">Search GRC events</label>
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          id={inputId}
          type="search"
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(e.target.value.length >= 4);
          }}
          onFocus={() => { if (query.length >= 4) setOpen(true); }}
          placeholder="Search (min 4 characters)"
          className="h-9 w-72 rounded border border-slate-300 pl-8 pr-8 text-sm focus:border-slate-500 focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Dropdown */}
        {open && results.length > 0 && (
          <ul
            id={listId}
            role="listbox"
            aria-label="Search results"
            className="absolute left-0 top-full z-50 mt-1 max-h-72 w-80 overflow-y-auto rounded border border-slate-200 bg-white shadow-lg"
          >
            {results.map((r) => (
              <li
                key={`${r.eventId}-${r.eventName}`}
                role="option"
                aria-selected={false}
                className="flex cursor-pointer flex-col gap-0.5 px-3 py-2 text-sm hover:bg-slate-50"
                onClick={() => handleSelect(r)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleSelect(r); }}
                tabIndex={0}
              >
                <span className="font-medium text-slate-900">{r.eventName}</span>
                <span className="text-xs text-slate-500">
                  {r.status} · {format(new Date(r.eventStartDate), "MM/dd/yyyy")} –{" "}
                  {format(new Date(r.eventEndDate), "MM/dd/yyyy")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Future/Past toggle */}
      <button
        type="button"
        onClick={() => setShowFuture((v) => !v)}
        className={cn(
          "rounded border px-2 py-1 text-xs font-medium",
          showFuture
            ? "border-slate-900 bg-slate-900 text-white"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        )}
        title={showFuture ? "Showing future events only – click to include past" : "Including past events"}
      >
        {showFuture ? "Future" : "All"}
      </button>

      {/* Advanced filter toggle */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        aria-label="Advanced search filters"
        className={cn(
          "flex items-center gap-1 rounded border px-2 py-1 text-xs",
          hasActiveAdvanced
            ? "border-slate-900 bg-slate-900 text-white"
            : "border-slate-300 text-slate-600 hover:bg-slate-50"
        )}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        {hasActiveAdvanced ? "Filtered" : "Advanced"}
      </button>

      {/* Advanced search flyout */}
      {showAdvanced && (
        <div className="absolute left-0 top-full z-50 mt-1 w-80 rounded border border-slate-200 bg-white p-4 shadow-lg">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Advanced Filters
          </p>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Meeting Name</label>
              <input
                type="text"
                value={advanced.meetingName ?? ""}
                onChange={(e) => setAdvanced((a) => ({ ...a, meetingName: e.target.value }))}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Meeting Type</label>
              <select
                value={advanced.meetingType ?? ""}
                onChange={(e) => setAdvanced((a) => ({ ...a, meetingType: e.target.value || undefined }))}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              >
                <option value="">All</option>
                {(searchData?.meetingTypes ?? []).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Planner</label>
              <select
                value={advanced.meetingPlannerName ?? ""}
                onChange={(e) => setAdvanced((a) => ({ ...a, meetingPlannerName: e.target.value || undefined }))}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              >
                <option value="">All</option>
                {(searchData?.meetingPlanners ?? []).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">Start Date</label>
                <input
                  type="date"
                  value={advanced.startDate ?? ""}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setAdvanced((a) => ({ ...a, startDate: val }));
                    validateDates(val, advanced.endDate);
                  }}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">End Date</label>
                <input
                  type="date"
                  value={advanced.endDate ?? ""}
                  min={advanced.startDate ?? undefined}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setAdvanced((a) => ({ ...a, endDate: val }));
                    validateDates(advanced.startDate, val);
                  }}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                />
              </div>
            </div>
            {dateError && <p className="text-xs text-red-600">{dateError}</p>}
          </div>
          <div className="mt-4 flex justify-between">
            <button
              type="button"
              onClick={() => { setAdvanced({}); setDateError(null); }}
              className="text-xs text-slate-500 underline hover:text-slate-900"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setShowAdvanced(false)}
              disabled={!!dateError}
              className="rounded border border-slate-900 bg-slate-900 px-3 py-1 text-xs text-white disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
